import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal, Select, Typography, Alert, Form, message } from 'antd';
import { setVaultPatientOptOut, updateVaultPatient } from '../../actions/identifierVaultActions';
import { VAULT_PATIENT_OPT_OUT_RESET, VAULT_PATIENT_UPDATE_RESET } from '../../constants/identifierVaultConstants';

const { Text, Paragraph } = Typography;

// Protocol §5.3: withdrawal erases identifiers but keeps the Salt and Study Hash so the orphaned
// hash can still block a re-add. The operator is told exactly what is erased and what is kept
// before they confirm, because none of it can be undone from the UI.
const ERASED_FIELDS = [
    'NHS Number', 'CHI Number', 'Date of birth', 'First name', 'Middle name',
    'Last name', 'Address', 'Postcode', 'Site-Study IDs', 'External PROM IDs',
];

const OPT_OUT_SOURCES = [
    { value: 'NDOO', label: 'National Data Opt-Out (NDOO)' },
    { value: 'CareTeam', label: 'Clinical care team' },
    { value: 'StudyWebsite', label: 'Study website / participant request' },
];

const OptOutModal = ({ open, onClose, participantId, participantLabel, onCompleted }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [optOutSource, setOptOutSource] = useState('NDOO');
    const { loading, error, success } = useSelector((state) => state.vaultPatientOptOut || {});

    // The thunk resolves either way, so the outcome is read from the slice it writes.
    useEffect(() => {
        if (!success) return;
        message.success(t('Withdrawal recorded. Identifiers erased; Study Hash retained.'));
        dispatch({ type: VAULT_PATIENT_OPT_OUT_RESET });
        if (onCompleted) onCompleted();
        onClose();
    }, [success]);

    const handleConfirm = () => dispatch(setVaultPatientOptOut(participantId, optOutSource));

    return (
        <Modal
            open={open}
            title={t('Record withdrawal (opt-out)')}
            okText={t('Erase identifiers and withdraw')}
            okButtonProps={{ danger: true, loading }}
            cancelText={t('Cancel')}
            onOk={handleConfirm}
            onCancel={onClose}
            destroyOnClose
        >
            {error && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={Array.isArray(error) ? error.join(' ') : String(error)} />}

            {participantLabel && (
                <Paragraph>
                    <Text strong>{t('Participant')}:</Text> {participantLabel}
                </Paragraph>
            )}

            <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('This cannot be undone')}
                description={
                    <>
                        <Paragraph style={{ marginBottom: 8 }}>
                            {t('The following will be permanently erased from the identifier vault and from this organisation\'s patient record')}:
                        </Paragraph>
                        <Paragraph style={{ marginBottom: 8 }}>
                            <Text code>{ERASED_FIELDS.map((f) => t(f)).join(', ')}</Text>
                        </Paragraph>
                        <Paragraph style={{ marginBottom: 0 }}>
                            {t('The Study Hash and its Salt are kept so this participant cannot be silently re-added later (protocol §5.3).')}
                        </Paragraph>
                    </>
                }
            />

            <Form layout="vertical">
                <Form.Item label={t('Where did the opt-out come from?')} required>
                    <Select
                        value={optOutSource}
                        onChange={setOptOutSource}
                        options={OPT_OUT_SOURCES.map((o) => ({ value: o.value, label: t(o.label) }))}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export const RecordConsentModal = ({ open, onClose, participantId, participantLabel, onCompleted }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { loading, error, success } = useSelector((state) => state.vaultPatientUpdate || {});

    useEffect(() => {
        if (!success) return;
        message.success(t('Research opt-in recorded.'));
        dispatch({ type: VAULT_PATIENT_UPDATE_RESET });
        if (onCompleted) onCompleted();
        onClose();
    }, [success]);

    const handleConfirm = () => dispatch(updateVaultPatient(participantId, {
        isOptedIn: true,
        optInConsentDate: new Date().toISOString(),
    }));

    return (
        <Modal
            open={open}
            title={t('Record research opt-in')}
            okText={t('Record consent')}
            okButtonProps={{ loading }}
            cancelText={t('Cancel')}
            onOk={handleConfirm}
            onCancel={onClose}
            destroyOnClose
        >
            {error && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={Array.isArray(error) ? error.join(' ') : String(error)} />}
            {participantLabel && (
                <Paragraph>
                    <Text strong>{t('Participant')}:</Text> {participantLabel}
                </Paragraph>
            )}
            <Paragraph>
                {t('This records that the participant has consented to OpenEP|NET research processing in the identifier vault. It is not a clinical consent form — those stay on the patient record.')}
            </Paragraph>
        </Modal>
    );
};

export default OptOutModal;
